import { useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea, Brush,
} from "recharts";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { useBandVars } from "../hooks/useNorms";
import type {
  AutoCatalog, AutoKpi, AutoKpiMetric, AutoSeries, AutoAnomalies, AutoAnomaly,
  AutoImportRow, AutoMetric, AutoSnapshot, AutoJumps, AutoJump, AutoVBand, AutoVbandDaily, FileRow,
} from "../types";
import PeriodCard from "../components/PeriodCard";
import PeriodPicker from "../components/PeriodPicker";
import ReactTile from "../components/ReactTile";
import { HistoryPeriod, useOpenFile } from "../components/FilesList";
import { apiWindow } from "../utils/period";
import { useApp } from "../state/app";

type Sub = "overview" | "charts" | "anomalies" | "jumps";

// Telemetry timestamps are station wall-clock tagged UTC — slice the ISO
// string directly so the browser timezone never shifts them.
const fmtTs = (iso: string | null) => iso ? iso.slice(5, 16).replace("T", " ") : "—";
const fmtTime = (iso: string | null) => iso ? iso.slice(11, 16) : "—";
const fmtFull = (iso: string | null) => iso ? iso.slice(0, 16).replace("T", " ") : "—";
const fmtNum = (v: number | null | undefined, d = 1) =>
  v === null || v === undefined ? "—" : (Number.isInteger(v) ? String(v) : v.toFixed(d));


// Operator reference for line voltage: nominal 20 kV, ±5% acceptable band.
const V_NOMINAL_KV = 20;
const V_BAND_PCT = 5;
const LINE_V_KEYS = new Set(["v12", "v23", "v31"]);
// Reference band for a metric in the given display scale (1 = native volts,
// 0.001 = kV). null if the metric isn't a line voltage.
function vBand(metricKey: string, scale = 1): { lo: number; nom: number; hi: number } | null {
  if (!LINE_V_KEYS.has(metricKey)) return null;
  const nom = V_NOMINAL_KV * 1000 * scale;
  return { lo: nom * (1 - V_BAND_PCT / 100), nom, hi: nom * (1 + V_BAND_PCT / 100) };
}
// Recharts band overlay (shaded ±5% area + nominal/limit lines). Returned as an
// array of elements — recharts flattens it like a .map(), same as dayMarks.
function vBandElems(band: { lo: number; nom: number; hi: number } | null, yAxisId?: string) {
  if (!band) return null;
  const idp = yAxisId ? { yAxisId } : {};
  return [
    <ReferenceArea key="vb-area" {...idp} y1={band.lo} y2={band.hi} fill="#34c759" fillOpacity={0.08} ifOverflow="extendDomain" />,
    <ReferenceLine key="vb-nom" {...idp} y={band.nom} stroke="#34c759" strokeDasharray="4 3" ifOverflow="extendDomain"
      label={{ value: `${V_NOMINAL_KV} кВ ±${V_BAND_PCT}%`, position: "insideRight", fontSize: 9, fill: "#34c759" }} />,
    <ReferenceLine key="vb-hi" {...idp} y={band.hi} stroke="#ff9500" strokeDasharray="2 4" ifOverflow="extendDomain" />,
    <ReferenceLine key="vb-lo" {...idp} y={band.lo} stroke="#ff9500" strokeDasharray="2 4" ifOverflow="extendDomain" />,
  ];
}

// Custom chart tooltip — the recharts default loses the date header on the
// dark theme. Shows date+time clearly, then each series colour-matched.
function ChartTooltip({ active, payload }: {
  active?: boolean;
  payload?: { dataKey?: string; name?: string; value?: number; color?: string; payload?: { full?: string } }[];
}) {
  if (!active || !payload || payload.length === 0) return null;
  const full = payload[0]?.payload?.full ?? "";
  return (
    <div style={{
      background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8,
      padding: "8px 10px", fontSize: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: "var(--text)" }}>{full}</div>
      {payload.map(s => (
        <div key={s.dataKey} style={{ color: s.color }}>{s.name}: <b>{fmtNum(s.value)}</b></div>
      ))}
    </div>
  );
}

export default function Automatika() {
  const { t } = useT();
  const [sub, setSub] = useState<Sub>("overview");
  const { paSel: sel, setPaSel: setSel } = useApp();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [catalog, setCatalog] = useState<AutoCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);

  const unit = 1;
  // API window: exclusive end = the selection's last second + 1.
  const win = useMemo(() => apiWindow(sel), [sel]);
  const qs = useMemo(() => {
    const p = new URLSearchParams({ unit: String(unit) });
    if (win.from) p.set("from", win.from);
    if (win.to) p.set("to", win.to);
    return p.toString();
  }, [win]);

  useEffect(() => {
    api.get<AutoCatalog>("/api/auto/metrics").then(setCatalog).catch(() => undefined);
  }, []);

  const subs: { k: Sub; label: string }[] = [
    { k: "overview",  label: t("auto.sub_overview") },
    { k: "charts",    label: t("auto.sub_charts") },
    { k: "anomalies", label: t("auto.sub_minmax") },
    { k: "jumps",     label: t("auto.sub_jumps") },
  ];

  return (
    <div>
      {/* period: the same card/picker as G1/G2 (per-minute data here, no PDF yet) */}
      <PeriodCard label={t("telemetry.src_pa")} sel={sel} catalog={catalog} onChange={() => setPickerOpen(true)} />
      {pickerOpen && (
        <PeriodPicker label={t("telemetry.src_pa")} catalog={catalog} value={sel} showFiles={false} showStep={false}
          onApply={s => { setSel(s); setPickerOpen(false); }} onClose={() => setPickerOpen(false)} />
      )}

      <div className="period-tabs" style={{ marginBottom: 12 }}>
        {subs.map(s => (
          <button key={s.k} className={"period-tab" + (sub === s.k ? " active" : "")}
            onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setSub(s.k); }}>
            {s.label}
          </button>
        ))}
      </div>

      {error && <div className="alert error">{error}</div>}

      {sub === "overview"  && <Overview qs={qs} catalog={catalog} onErr={setError} />}
      {sub === "charts"    && <ChartsTab win={win} catalog={catalog} onErr={setError} />}
      {sub === "anomalies" && <MinMaxTab win={win} onErr={setError} />}
      {sub === "jumps"     && <JumpsTab qs={qs} unit={unit} catalog={catalog} onErr={setError} />}
    </div>
  );
}

// ---------------- Overview ----------------

function Overview({ qs, catalog, onErr }: {
  qs: string; catalog: AutoCatalog | null; onErr: (m: string) => void;
}) {
  const { t } = useT();
  const [kpi, setKpi] = useState<AutoKpi | null>(null);
  const [vband, setVband] = useState<AutoVBand | null>(null);
  const [detail, setDetail] = useState<
    { key: string; title: string; unit: string; scale: number; stats?: AutoKpiMetric } | null
  >(null);
  const [multi, setMulti] = useState<
    { keys: string[]; names: string[]; title: string; showBand: boolean } | null
  >(null);
  const [devLine, setDevLine] = useState<{ key: string; label: string } | null>(null);
  useEffect(() => {
    setKpi(null); setVband(null);
    api.get<AutoKpi>(`/api/auto/kpi?${qs}`).then(setKpi).catch((e: ApiError) => onErr(e.message));
    api.get<AutoVBand>(`/api/auto/vband?${qs}`).then(setVband).catch(() => undefined);
  }, [qs]);

  if (!kpi) return <div className="empty-state"><span className="spinner" /></div>;
  const m = kpi.metrics;

  const kv = (x?: number | null) => (x == null ? null : x / 1000);
  const openDetail = (key: string, title: string, unit: string, scale: number) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    setDetail({ key, title, unit, scale, stats: m[key] });
  };
  const openMulti = (cfg: { keys: string[]; names: string[]; title: string; showBand: boolean }) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    setMulti(cfg);
  };

  // Combined voltage tile: each line's min–max (kV). Red border + "N отклонений"
  // when any reading left the 20 kV ±5% band (line voltages only).
  const vTile = (
    title: string, lines: { key: string; label: string }[], showBand: boolean, onClick: () => void,
  ) => {
    const b = showBand ? vBand("v12") : null;
    const isOut = (mm?: AutoKpiMetric) =>
      !!b && ((mm?.min != null && mm.min < b.lo) || (mm?.max != null && mm.max > b.hi));
    const anyOut = lines.some(l => isOut(m[l.key]));
    const oob = showBand ? (vband?.total ?? 0) : 0;
    return (
      <div className="auto-tile kv" onClick={onClick}
        style={{ cursor: "pointer", ...(anyOut ? { border: "1.6px solid #ff453a" } : {}) }}>
        <div className="auto-tile-top">
          <span className="auto-tile-icon">🔌</span>
          <span className="auto-tile-title">{title}</span>
          <span style={{ marginLeft: "auto", opacity: 0.5 }}>›</span>
        </div>
        {showBand && oob > 0 && (
          <div style={{ color: "#ff453a", fontSize: 12, fontWeight: 700, margin: "2px 0" }}>
            ⚠ {t("auto.oob_n", { n: oob })}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
          {lines.map(l => {
            const mm = m[l.key];
            const out = isOut(mm);
            return (
              <div key={l.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 14 }}>
                <span style={{ color: out ? "#ff453a" : "var(--hint)", fontWeight: 600 }}>{l.label}</span>
                <span style={{ color: out ? "#ff453a" : undefined }}>
                  <b>{fmtNum(kv(mm?.min), 1)}</b> – <b>{fmtNum(kv(mm?.max), 1)}</b>
                  <span style={{ color: "var(--hint)", fontSize: 11 }}> кВ</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const minMaxTile = (cls: string, icon: string, title: string, mn: number | null, mx: number | null, d: number, onClick?: () => void) => (
    <div className={`auto-tile ${cls}`} onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
      <div className="auto-tile-top">
        <span className="auto-tile-icon">{icon}</span>
        <span className="auto-tile-title">{title}</span>
        {onClick && <span style={{ marginLeft: "auto", opacity: 0.5 }}>›</span>}
      </div>
      <div className="auto-tile-mm">
        <div className="auto-tile-col"><span className="auto-tile-num">{fmtNum(mn, d)}</span><span className="auto-tile-pill">min</span></div>
        <div className="auto-tile-col"><span className="auto-tile-num">{fmtNum(mx, d)}</span><span className="auto-tile-pill">max</span></div>
      </div>
    </div>
  );

  // Single line-voltage tile (V12/V23/V31): green min–max in kV; reddens + shows
  // its own out-of-band minute count (20 kV ±5%) when that line leaves the band.
  // Click → that line's own detail chart (band drawn there too).
  const lineTile = (key: string, label: string) => {
    const mm = m[key];
    const b = vBand(key); // native-V band {lo, nom, hi}
    const oob = vband?.per_metric?.[key] ?? 0;
    const minOut = !!b && mm?.min != null && mm.min < b.lo;
    const maxOut = !!b && mm?.max != null && mm.max > b.hi;
    const out = oob > 0 || minOut || maxOut;
    return (
      <div className="auto-tile kv" onClick={() => openDetail(key, label, "кВ", 0.001)}
        style={{ cursor: "pointer", ...(out ? { border: "1.6px solid #ff453a" } : {}) }}>
        <div className="auto-tile-top">
          <span className="auto-tile-icon">🔌</span>
          <span className="auto-tile-title">{label}</span>
          <span style={{ marginLeft: "auto", opacity: 0.5 }}>›</span>
        </div>
        {oob > 0 && (
          <div style={{ color: "#ff453a", fontSize: 12, fontWeight: 700, margin: "2px 0" }}>
            ⚠ {t("auto.oob_n", { n: oob })}
          </div>
        )}
        <div className="auto-tile-mm">
          <div className="auto-tile-col">
            <span className="auto-tile-num" style={minOut ? { color: "#ff453a" } : undefined}>{fmtNum(kv(mm?.min), 1)}</span>
            <span className="auto-tile-pill">min</span>
          </div>
          <div className="auto-tile-col">
            <span className="auto-tile-num" style={maxOut ? { color: "#ff453a" } : undefined}>{fmtNum(kv(mm?.max), 1)}</span>
            <span className="auto-tile-pill">max</span>
          </div>
        </div>
      </div>
    );
  };

  // Deviation-count tile (V12/V23/V31): out-of-band minute count over the
  // selected period; reddens when > 0. Click → per-day breakdown for that line.
  const devTile = (key: string, label: string) => {
    const n = vband?.per_metric?.[key] ?? 0;
    return (
      <div className="auto-tile" onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); setDevLine({ key, label }); }}
        style={{ cursor: "pointer", ...(n > 0 ? { border: "1.6px solid #ff453a" } : {}) }}>
        <div className="auto-tile-top">
          <span className="auto-tile-icon">⚠️</span>
          <span className="auto-tile-title">{label}</span>
          <span style={{ marginLeft: "auto", opacity: 0.5 }}>›</span>
        </div>
        <div className="auto-tile-bigrow">
          <span className="auto-tile-big" style={n > 0 ? { color: "#ff453a" } : undefined}>{n}</span>
          <span className="auto-tile-unit">{t("auto.dev_short")}</span>
        </div>
      </div>
    );
  };

  const big = kpi.energy_kwh >= 1000;

  return (
    <>
      <div className="auto-tiles" style={{ gridTemplateColumns: "1fr 1fr", marginBottom: 6 }}>
        {minMaxTile("kw", "⚡", "P, kW", m.p_total?.min ?? null, m.p_total?.max ?? null, 1,
          () => openDetail("p_total", "P", "kW", 1))}
        {minMaxTile("react", "🔄", "Q, kvar", m.q_total?.min ?? null, m.q_total?.max ?? null, 1,
          () => openDetail("q_total", "Q", "kvar", 1))}
        <div className="auto-tile energy">
          <div className="auto-tile-top"><span className="auto-tile-icon">🔋</span><span className="auto-tile-title">{t("auto.kpi_energy")}</span></div>
          <div className="auto-tile-bigrow">
            <span className="auto-tile-big">{big ? (kpi.energy_kwh / 1000).toFixed(1) : fmtNum(kpi.energy_kwh, 0)}</span>
            <span className="auto-tile-unit">{big ? t("auto.mwh") : t("auto.kwh")}</span>
          </div>
        </div>
        <ReactTile out={kpi.kvarh_out} inn={kpi.kvarh_in} />
      </div>
      <div style={{ fontSize: 12, color: "var(--hint)", margin: "0 2px" }}>{t("auto.react_hint")}</div>

      {/* Line voltages (left, with band/deviations) paired with phase voltages
          (right, P-kW-sized min/max tiles): V12|V1, V23|V2, V31|V3. */}
      <div className="auto-tiles" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 10 }}>
        {lineTile("v12", "V12")}
        {minMaxTile("kv", "🔌", "V1", kv(m.v1?.min), kv(m.v1?.max), 1, () => openDetail("v1", "V1", "кВ", 0.001))}
        {lineTile("v23", "V23")}
        {minMaxTile("kv", "🔌", "V2", kv(m.v2?.min), kv(m.v2?.max), 1, () => openDetail("v2", "V2", "кВ", 0.001))}
        {lineTile("v31", "V31")}
        {minMaxTile("kv", "🔌", "V3", kv(m.v3?.min), kv(m.v3?.max), 1, () => openDetail("v3", "V3", "кВ", 0.001))}
      </div>

      {/* Deviation counters per line (out-of-band minutes over the period). */}
      <div style={{ marginTop: 14, marginBottom: 6, fontWeight: 700, fontSize: 14 }}>{t("auto.dev_by_line")}</div>
      <div className="auto-tiles" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
        {devTile("v12", "V12")}
        {devTile("v23", "V23")}
        {devTile("v31", "V31")}
      </div>

      {(catalog?.groups ?? []).map(g => {
        const ms = (catalog?.metrics ?? []).filter(x => x.group === g.key);
        const hasData = ms.some(x => m[x.key] && (m[x.key].avg !== null || m[x.key].max !== null));
        if (!hasData) return null;
        return (
          <details key={g.key} className="cat-section" open>
            <summary className="cat-summary">
              <span className="cat-summary-arrow">▾</span>
              <span className="cat-summary-label">{g.label}</span>
            </summary>
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ fontSize: 13 }}>
                <thead><tr>
                  <th>{t("auto.col_metric")}</th><th>min</th><th>avg</th><th>max</th><th>last</th>
                </tr></thead>
                <tbody>
                  {ms.map(x => {
                    const d = m[x.key];
                    return (
                      <tr key={x.key}>
                        <td>{x.label}{x.unit ? <span style={{ color: "var(--hint)" }}> {x.unit}</span> : null}</td>
                        <td className="num">{fmtNum(d?.min)}</td>
                        <td className="num">{fmtNum(d?.avg)}</td>
                        <td className="num">{fmtNum(d?.max)}</td>
                        <td className="num">{fmtNum(d?.last)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        );
      })}

      {detail && (
        <MetricDetailModal metricKey={detail.key} title={detail.title} unit={detail.unit}
          scale={detail.scale} stats={detail.stats} qs={qs} onClose={() => setDetail(null)} />
      )}
      {multi && (
        <MultiLineModal keys={multi.keys} names={multi.names} title={multi.title}
          showBand={multi.showBand} qs={qs} onClose={() => setMulti(null)} />
      )}
      {devLine && (
        <DeviationDaysModal metricKey={devLine.key} label={devLine.label} qs={qs}
          onClose={() => setDevLine(null)} />
      )}
    </>
  );
}

// Per-day out-of-band breakdown for one line voltage, opened from a deviation
// tile: each day's count of out-of-band minutes + that day's worst min/max (kV),
// newest first. Scoped to the dashboard's selected timeframe (qs).
function DeviationDaysModal({ metricKey, label, qs, onClose }: {
  metricKey: string; label: string; qs: string; onClose: () => void;
}) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  const [data, setData] = useState<AutoVbandDaily | null>(null);
  useEffect(() => {
    const p = new URLSearchParams(qs);
    p.set("metric", metricKey);
    api.get<AutoVbandDaily>(`/api/auto/vband/daily?${p.toString()}`).then(setData).catch(() => undefined);
  }, [metricKey, qs]);

  const kvv = (x: number | null | undefined) => (x == null ? "—" : fmtNum(x / 1000, 1));
  const lo = data?.lo, hi = data?.hi;
  const total = (data?.days ?? []).reduce((s, d) => s + d.count, 0);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }}
      onClick={onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "85vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{label} · {t("auto.dev_by_line")}</h2>
        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 12 }}>
          {t("auto.dev_total")}: <b style={total > 0 ? { color: "#ff453a" } : undefined}>{total}</b>
          {lo != null && hi != null && <> · {t("auto.dev_band")}: {fmtNum(lo / 1000, 0)}–{fmtNum(hi / 1000, 0)} кВ</>}
        </div>
        {!data && <div className="empty-state"><span className="spinner" /></div>}
        {data && data.days.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
        {data && data.days.length > 0 && (
          <table className="table" style={{ fontSize: 13 }}>
            <thead><tr>
              <th>{t("auto.dev_day")}</th><th>{t("auto.dev_short")}</th><th>min</th><th>max</th>
            </tr></thead>
            <tbody>
              {data.days.map(d => {
                const minOut = lo != null && d.vmin != null && d.vmin < lo;
                const maxOut = hi != null && d.vmax != null && d.vmax > hi;
                return (
                  <tr key={d.day}>
                    <td>{d.day}</td>
                    <td className="num" style={d.count > 0 ? { color: "#ff453a", fontWeight: 700 } : { color: "var(--hint)" }}>{d.count}</td>
                    <td className="num" style={minOut ? { color: "#ff453a" } : undefined}>{kvv(d.vmin)}</td>
                    <td className="num" style={maxOut ? { color: "#ff453a" } : undefined}>{kvv(d.vmax)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

// Detail bottom-sheet opened from a KPI tile: min/avg/max/last + a chart over
// the selected timeframe. `scale` maps to display units (V→kV uses 0.001).
function MetricDetailModal({ metricKey, title, unit, scale, stats, qs, onClose }: {
  metricKey: string; title: string; unit: string; scale: number;
  stats?: AutoKpiMetric; qs: string; onClose: () => void;
}) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  const [series, setSeries] = useState<AutoSeries | null>(null);
  useEffect(() => {
    const p = new URLSearchParams(qs);
    p.set("metric", metricKey); p.set("bucket", "auto"); p.set("points", "500");
    api.get<AutoSeries>(`/api/auto/series?${p.toString()}`).then(setSeries).catch(() => undefined);
  }, [metricKey, qs]);

  const data = useMemo(() => (series?.points ?? []).map(pt => ({
    label: fmtTs(pt.t), full: fmtFull(pt.t),
    min: pt.min == null ? null : pt.min * scale,
    avg: pt.avg == null ? null : pt.avg * scale,
    max: pt.max == null ? null : pt.max * scale,
  })), [series, scale]);

  const sv = (x: number | null | undefined) => (x == null ? "—" : fmtNum(x * scale, 1));
  const cell = (lbl: string, val: string) => (
    <div className="auto-tile-col" style={{ alignItems: "center" }}>
      <span className="auto-tile-num">{val}</span><span className="auto-tile-pill">{lbl}</span>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }}
      onClick={onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "85vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{title}{unit ? `, ${unit}` : ""}</h2>
        <div className="auto-tile-mm" style={{ gap: 12, justifyContent: "space-around", marginBottom: 12 }}>
          {cell("min", sv(stats?.min))}
          {cell("avg", sv(stats?.avg))}
          {cell("max", sv(stats?.max))}
          {cell("last", sv(stats?.last))}
        </div>
        {!series && <div className="empty-state"><span className="spinner" /></div>}
        {series && data.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
        {series && data.length > 0 && (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={28} />
              <YAxis tick={{ fontSize: 10 }} width={44} domain={["auto", "auto"]} />
              <Tooltip content={<ChartTooltip />} />
              {vBandElems(vBand(metricKey, scale))}
              <Line type="monotone" dataKey="max" name="max" stroke="#ff9500" strokeWidth={1} dot={false} isAnimationActive={false} opacity={0.5} />
              <Line type="monotone" dataKey="avg" name="avg" stroke="#2481cc" strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="min" name="min" stroke="#34c759" strokeWidth={1} dot={false} isAnimationActive={false} opacity={0.5} />
            </LineChart>
          </ResponsiveContainer>
        )}
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

// Combined voltage detail: up to 3 lines on one chart (kV) + each line's
// min/max. Line-voltage charts show the 20 kV ±5% band; phase charts don't.
const MULTI_COLORS = ["#2481cc", "#34c759", "#ff9500"];
function MultiLineModal({ keys, names, title, showBand, qs, onClose }: {
  keys: string[]; names: string[]; title: string; showBand: boolean; qs: string; onClose: () => void;
}) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  const [series, setSeries] = useState<Record<string, AutoSeries> | null>(null);
  const keyStr = keys.join(",");
  useEffect(() => {
    Promise.all(keys.map(k => {
      const p = new URLSearchParams(qs);
      p.set("metric", k); p.set("bucket", "auto"); p.set("points", "500");
      return api.get<AutoSeries>(`/api/auto/series?${p.toString()}`).then(s => [k, s] as const);
    })).then(arr => setSeries(Object.fromEntries(arr) as Record<string, AutoSeries>)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs, keyStr]);

  const data = useMemo(() => {
    if (!series) return [];
    const byT = new Map<string, Record<string, number | null | string>>();
    keys.forEach(k => {
      for (const pt of series[k]?.points ?? []) {
        const row = byT.get(pt.t) ?? { t: pt.t, label: fmtTs(pt.t), full: fmtFull(pt.t) };
        row[k] = pt.avg == null ? null : pt.avg / 1000;
        byT.set(pt.t, row);
      }
    });
    return [...byT.values()].sort((a, b) => ((a.t as string) < (b.t as string) ? -1 : 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, keyStr]);

  const stats = keys.map(k => {
    const pts = series?.[k]?.points ?? [];
    const mins = pts.map(p => p.min).filter((x): x is number => x != null);
    const maxs = pts.map(p => p.max).filter((x): x is number => x != null);
    return { min: mins.length ? Math.min(...mins) / 1000 : null, max: maxs.length ? Math.max(...maxs) / 1000 : null };
  });
  const band = showBand ? vBand("v12", 0.001) : null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }}
      onClick={onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "92vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 10 }}>
          {keys.map((k, i) => {
            const s = stats[i];
            const out = !!band && ((s.min != null && s.min < band.lo) || (s.max != null && s.max > band.hi));
            return (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 14 }}>
                <span style={{ color: MULTI_COLORS[i % 3], fontWeight: 700 }}>● {names[i]}</span>
                <span style={{ color: out ? "#ff453a" : undefined }}>
                  <b>{fmtNum(s.min, 1)}</b> – <b>{fmtNum(s.max, 1)}</b>
                  <span style={{ color: "var(--hint)", fontSize: 11 }}> кВ</span>
                </span>
              </div>
            );
          })}
        </div>
        {!series && <div className="empty-state"><span className="spinner" /></div>}
        {series && data.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
        {series && data.length > 0 && (
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={28} />
              <YAxis tick={{ fontSize: 10 }} width={44} domain={["auto", "auto"]} />
              <Tooltip content={<ChartTooltip />} />
              {vBandElems(band)}
              {keys.map((k, i) => (
                <Line key={k} type="monotone" dataKey={k} name={names[i]} stroke={MULTI_COLORS[i % 3]}
                  strokeWidth={1.5} dot={false} isAnimationActive={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

// ---------------- Charts ----------------

const RES_OPTIONS = ["auto", "60", "30", "15", "5", "1", "raw"];

// Red dot on points whose interval contained a deviation (flag from backend).
function renderFlagDot(props: { cx?: number; cy?: number; index?: number; payload?: { flag?: boolean } }) {
  const { cx, cy, index, payload } = props;
  if (payload?.flag && cx != null && cy != null) {
    return <circle key={index} cx={cx} cy={cy} r={4} fill="#ff453a" stroke="#fff" strokeWidth={1} />;
  }
  return <circle key={index} cx={cx ?? 0} cy={cy ?? 0} r={0} fill="none" />;
}

function ChartsTab({ win, catalog, onErr }: {
  win: { from: string; to: string }; catalog: AutoCatalog | null; onErr: (m: string) => void;
}) {
  const { t } = useT();
  const [metric, setMetric] = useState("p_total");
  const [res, setRes] = useState("auto");
  // Drill-down is a stack of [from,to] windows, so the user can dive level by
  // level and step back out one at a time. Empty = the top-level timeframe.
  const [stack, setStack] = useState<{ from: string; to: string }[]>([]);
  const [brush, setBrush] = useState<{ startIndex?: number; endIndex?: number } | null>(null);
  const [series, setSeries] = useState<AutoSeries | null>(null);
  const [overlay, setOverlay] = useState("");            // current metric on the right axis ("" = off)
  const [ovSeries, setOvSeries] = useState<AutoSeries | null>(null);

  // Changing the top timeframe exits any drill-down.
  useEffect(() => { setStack([]); setBrush(null); }, [win.from, win.to]);

  const eff = stack.length ? stack[stack.length - 1] : win;
  useEffect(() => {
    setSeries(null); setBrush(null);
    const p = new URLSearchParams({ unit: "1" });
    if (eff.from) p.set("from", eff.from);
    if (eff.to) p.set("to", eff.to);
    p.set("metric", metric); p.set("bucket", res); p.set("points", "700");
    api.get<AutoSeries>(`/api/auto/series?${p.toString()}`).then(setSeries).catch((e: ApiError) => onErr(e.message));
  }, [eff.from, eff.to, metric, res]);

  // Optional second metric (a current) overlaid on the right Y-axis, to see if
  // current spikes line up in time with voltage spikes.
  useEffect(() => {
    if (!overlay) { setOvSeries(null); return; }
    const p = new URLSearchParams({ unit: "1" });
    if (eff.from) p.set("from", eff.from);
    if (eff.to) p.set("to", eff.to);
    p.set("metric", overlay); p.set("bucket", res); p.set("points", "700");
    api.get<AutoSeries>(`/api/auto/series?${p.toString()}`).then(setOvSeries).catch(() => undefined);
  }, [overlay, eff.from, eff.to, res]);

  // Voltage reference band (20 kV ±5%) for line-voltage metrics, in native volts.
  const band = vBand(metric);
  const bandLo = band?.lo ?? null;
  const bandHi = band?.hi ?? null;

  const data = useMemo(() => {
    const ovMap = new Map<string, number | null>();
    for (const pt of ovSeries?.points ?? []) ovMap.set(pt.t, pt.avg);
    return (series?.points ?? []).map(pt => {
      const oob = bandLo != null && bandHi != null && pt.avg != null && (pt.avg < bandLo || pt.avg > bandHi);
      return {
        label: fmtTs(pt.t), full: fmtFull(pt.t), t: pt.t,
        min: pt.min, avg: pt.avg, max: pt.max, flag: !!pt.flag || oob,
        ov: ovMap.get(pt.t) ?? null,
      };
    });
  }, [series, ovSeries, bandLo, bandHi]);

  const flaggedCount = useMemo(() => data.filter(d => d.flag).length, [data]);

  // Vertical date dividers wherever the calendar day changes — so the
  // compressed (operating-only) timeline still shows every date clearly.
  const dayMarks = useMemo(() => {
    const out: { label: string; day: string }[] = [];
    let prev = "";
    for (const d of data) {
      const day = d.t ? d.t.slice(0, 10) : "";
      if (day && day !== prev) { out.push({ label: d.label, day: day.slice(5) }); prev = day; }
    }
    return out;
  }, [data]);

  const pushDrill = (from: string, to: string) => {
    setStack(s => [...s, { from, to }]);
    setRes("auto"); setBrush(null);
    tg()?.HapticFeedback?.impactOccurred("medium");
  };
  // Tap any point → zoom to a window centred on it, a few buckets wide.
  const drillPoint = (pt: { t?: string }) => {
    if (!series || !pt?.t) return;
    const half = Math.max((series.bucket_min || 30) * 5, 10) * 60 * 1000;
    const c = new Date(pt.t).getTime();
    pushDrill(new Date(c - half).toISOString(), new Date(c + half).toISOString());
  };
  // Brush range → zoom to exactly the dragged span.
  const zoomToBrush = () => {
    if (!brush || brush.startIndex == null || brush.endIndex == null) return;
    const a = data[brush.startIndex]?.t, b = data[brush.endIndex]?.t;
    if (!a || !b || a === b) return;
    const to = new Date(new Date(b).getTime() + (series?.bucket_min || 1) * 60 * 1000).toISOString();
    pushDrill(a, to);
  };
  const canZoomBrush = !!brush && brush.startIndex != null && brush.endIndex != null
    && brush.endIndex - brush.startIndex >= 1
    && (brush.startIndex !== 0 || brush.endIndex !== data.length - 1);

  const resLabel = (r: string) =>
    r === "auto" ? t("auto.res_auto") : r === "raw" ? t("auto.res_raw") : `${r} ${t("auto.min_short")}`;
  const currents = (catalog?.metrics ?? []).filter(x => x.group === "current");
  const ovLabel = currents.find(x => x.key === overlay)?.label ?? overlay;

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <select value={metric} onChange={e => setMetric(e.target.value)} style={{ width: "100%" }}>
          {(catalog?.groups ?? []).map(g => (
            <optgroup key={g.key} label={g.label}>
              {(catalog?.metrics ?? []).filter(x => x.group === g.key).map((x: AutoMetric) => (
                <option key={x.key} value={x.key}>{x.label}{x.unit ? ` (${x.unit})` : ""}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--hint)" }}>{t("auto.resolution")}:</span>
          <select value={res} onChange={e => setRes(e.target.value)} style={{ flex: 1, minWidth: 110 }}>
            {RES_OPTIONS.map(r => <option key={r} value={r}>{resLabel(r)}</option>)}
          </select>
          {stack.length > 0 && (
            <>
              <button className="btn secondary" style={{ padding: "6px 10px" }}
                onClick={() => { setStack(s => s.slice(0, -1)); setBrush(null); tg()?.HapticFeedback?.impactOccurred("light"); }}>
                {t("auto.back")}
              </button>
              <button className="btn secondary" style={{ padding: "6px 10px" }}
                onClick={() => { setStack([]); setRes("auto"); setBrush(null); tg()?.HapticFeedback?.impactOccurred("light"); }}>
                {t("auto.reset_zoom")}
              </button>
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--hint)" }}>{t("charts.overlay")}:</span>
          <select value={overlay} onChange={e => setOverlay(e.target.value)} style={{ flex: 1, minWidth: 110 }}>
            <option value="">{t("charts.overlay_none")}</option>
            {currents.map(c => <option key={c.key} value={c.key}>{c.label}{c.unit ? ` (${c.unit})` : ""}</option>)}
          </select>
        </div>
      </div>

      {!series && <div className="empty-state"><span className="spinner" /></div>}
      {series && data.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
      {series && data.length > 0 && (
        <div className="card">
          <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 6 }}>
            {series.label}{series.unit ? `, ${series.unit}` : ""} ·{" "}
            {series.resolution === "raw" ? t("auto.res_raw")
              : `${t("auto.bucket")} ${series.bucket_min} ${t("auto.min_short")}`}
            {stack.length > 0 && (
              <span> · 🔎 {fmtTs(data[0]?.t)} → {fmtTime(data[data.length - 1]?.t)}</span>
            )}
            {overlay && <span style={{ color: "#af52de" }}> · 🟣 {ovLabel} ({t("charts.right_axis")})</span>}
            {flaggedCount > 0 && (
              <span style={{ color: "#ff453a" }}> · 🔴 {t("auto.deviations_n", { n: flaggedCount })}</span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={data} margin={{ top: 14, right: 10, left: 0, bottom: 4 }}
              onClick={(st: { activePayload?: { payload?: { t?: string } }[] }) => {
                const p = st?.activePayload?.[0]?.payload;
                if (p) drillPoint(p);
              }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={28} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={44} />
              {overlay && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={40} stroke="#af52de" />}
              <Tooltip content={<ChartTooltip />} />
              {vBandElems(band, "left")}
              {dayMarks.map(dm => (
                <ReferenceLine key={dm.label} yAxisId="left" x={dm.label} stroke="var(--hint)" strokeDasharray="2 4" opacity={0.55}
                  label={{ value: dm.day, position: "insideTop", fontSize: 9, fill: "var(--hint)" }} />
              ))}
              <Line yAxisId="left" type="monotone" dataKey="max" name="max" stroke="#ff9500" strokeWidth={1}
                dot={false} isAnimationActive={false} opacity={0.5} />
              <Line yAxisId="left" type="monotone" dataKey="avg" name="avg" stroke="#2481cc" strokeWidth={2}
                dot={data.length <= 1500 ? renderFlagDot : false} isAnimationActive={false} />
              <Line yAxisId="left" type="monotone" dataKey="min" name="min" stroke="#34c759" strokeWidth={1}
                dot={false} isAnimationActive={false} opacity={0.5} />
              {overlay && <Line yAxisId="right" type="monotone" dataKey="ov" name={ovLabel} stroke="#af52de"
                strokeWidth={1.5} dot={false} isAnimationActive={false} />}
              {data.length > 2 && (
                <Brush dataKey="label" height={22} travellerWidth={10} stroke="var(--link, #2481cc)"
                  tickFormatter={() => ""} onChange={(r: { startIndex?: number; endIndex?: number }) => setBrush(r)} />
              )}
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            <button className="btn secondary" style={{ padding: "6px 10px", opacity: canZoomBrush ? 1 : 0.5 }}
              disabled={!canZoomBrush} onClick={zoomToBrush}>
              🔍 {t("auto.zoom_to_sel")}
            </button>
            <span style={{ fontSize: 11, color: "var(--hint)" }}>{t("auto.drill_hint")}</span>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------- Min/Max by time (drill-down) ----------------

const MM_DRILL = [60, 5, 1];   // bucket sizes in minutes: hour → 5 min → minute
const MM_LINES = [{ key: "v12", name: "V12" }, { key: "v23", name: "V23" }, { key: "v31", name: "V31" }];

function MinMaxTab({ win, onErr }: { win: { from: string; to: string }; onErr: (m: string) => void }) {
  const { t } = useT();
  const bv = useBandVars();
  // Drill stack: each entry = window+bucket for that level. Empty = top
  // (hourly over the selected timeframe).
  const [stack, setStack] = useState<{ bucketMin: number; from: string; to: string }[]>([]);
  const [series, setSeries] = useState<Record<string, AutoSeries> | null>(null);

  useEffect(() => { setStack([]); }, [win.from, win.to]);

  const level = stack.length ? stack[stack.length - 1] : { bucketMin: MM_DRILL[0], from: win.from, to: win.to };
  useEffect(() => {
    setSeries(null);
    Promise.all(MM_LINES.map(v => {
      const p = new URLSearchParams({ unit: "1", metric: v.key, bucket: String(level.bucketMin), points: "2000" });
      if (level.from) p.set("from", level.from);
      if (level.to) p.set("to", level.to);
      return api.get<AutoSeries>(`/api/auto/series?${p.toString()}`).then(s => [v.key, s] as const);
    })).then(arr => setSeries(Object.fromEntries(arr) as Record<string, AutoSeries>))
      .catch((e: ApiError) => onErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level.bucketMin, level.from, level.to]);

  type Cell = { min: number | null; max: number | null };
  const rows = useMemo(() => {
    if (!series) return [] as { t: string; cells: Record<string, Cell> }[];
    const byT = new Map<string, Record<string, Cell>>();
    for (const v of MM_LINES) {
      for (const pt of series[v.key]?.points ?? []) {
        const row = byT.get(pt.t) ?? {};
        row[v.key] = { min: pt.min, max: pt.max };
        byT.set(pt.t, row);
      }
    }
    return [...byT.entries()].map(([tt, cells]) => ({ t: tt, cells }))
      .sort((a, b) => (a.t < b.t ? 1 : -1)); // newest first
  }, [series]);

  const nextBucket = (b: number) => {
    const i = MM_DRILL.indexOf(b);
    return i >= 0 && i < MM_DRILL.length - 1 ? MM_DRILL[i + 1] : null;
  };
  const isLeaf = nextBucket(level.bucketMin) == null;
  const drill = (rowT: string) => {
    const nb = nextBucket(level.bucketMin);
    if (nb == null) return;
    const to = new Date(new Date(rowT).getTime() + level.bucketMin * 60000).toISOString();
    setStack(s => [...s, { bucketMin: nb, from: rowT, to }]);
    tg()?.HapticFeedback?.impactOccurred("medium");
  };

  const band = vBand("v12"); // native V {lo:19000, hi:21000}
  const kvv = (x?: number | null) => (x == null ? "—" : fmtNum(x / 1000, 2));
  const isLo = (c?: Cell) => !!c && c.min != null && band != null && c.min < band.lo;
  const isHi = (c?: Cell) => !!c && c.max != null && band != null && c.max > band.hi;

  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "0 0 8px" }}>
        <span style={{ fontSize: 12, color: "var(--hint)" }}>
          {t("minmax.bucket")}: {level.bucketMin} {t("auto.min_short")}
        </span>
        {stack.length > 0 && (
          <button className="btn secondary" style={{ padding: "4px 10px" }}
            onClick={() => { setStack(s => s.slice(0, -1)); tg()?.HapticFeedback?.impactOccurred("light"); }}>
            {t("auto.back")}
          </button>
        )}
        {stack.length > 0 && (
          <button className="btn secondary" style={{ padding: "4px 10px" }}
            onClick={() => { setStack([]); tg()?.HapticFeedback?.impactOccurred("light"); }}>
            {t("auto.reset_zoom")}
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: "var(--hint)", marginBottom: 8 }}>
        {isLeaf ? t("minmax.hint_leaf", bv) : t("minmax.hint", bv)}
      </div>

      {!series && <div className="empty-state"><span className="spinner" /></div>}
      {series && rows.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
      {series && rows.length > 0 && (
        <div className="card" style={{ overflowX: "auto" }}>
          <table className="table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th rowSpan={2}>{t("minmax.time")}</th>
                {MM_LINES.map(v => <th key={v.key} colSpan={2} style={{ textAlign: "center" }}>{v.name}</th>)}
              </tr>
              <tr>
                {MM_LINES.flatMap(v => [
                  <th key={v.key + "mn"}>min</th>,
                  <th key={v.key + "mx"}>max</th>,
                ])}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const anyOut = MM_LINES.some(v => isLo(r.cells[v.key]) || isHi(r.cells[v.key]));
                return (
                  <tr key={r.t} onClick={() => !isLeaf && drill(r.t)}
                    style={{ cursor: isLeaf ? "default" : "pointer", background: anyOut ? "rgba(255,69,58,0.10)" : undefined }}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {fmtFull(r.t)}{!isLeaf && <span style={{ color: "var(--link, #2481cc)" }}> ›</span>}
                    </td>
                    {MM_LINES.flatMap(v => {
                      const c = r.cells[v.key];
                      return [
                        <td key={v.key + "mn"} className="num" style={{ color: isLo(c) ? "#ff453a" : undefined }}>{kvv(c?.min)}</td>,
                        <td key={v.key + "mx"} className="num" style={{ color: isHi(c) ? "#ff453a" : undefined }}>{kvv(c?.max)}</td>,
                      ];
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------- Anomalies ----------------

function AnomaliesTab({ qs, unit, catalog, onErr }: {
  qs: string; unit: number; catalog: AutoCatalog | null; onErr: (m: string) => void;
}) {
  const { t } = useT();
  const [data, setData] = useState<AutoAnomalies | null>(null);
  const [hideZeros, setHideZeros] = useState(false);
  const [metricFilter, setMetricFilter] = useState("");
  const [sel, setSel] = useState<AutoAnomaly | null>(null);

  useEffect(() => {
    setData(null);
    api.get<AutoAnomalies>(`/api/auto/anomalies?${qs}&sigma=2`).then(setData).catch((e: ApiError) => onErr(e.message));
  }, [qs]);

  // base = events after the hide-zeros toggle; drives both the filter and list.
  const base = useMemo(
    () => (data?.events ?? []).filter(e => !hideZeros || e.peak !== 0),
    [data, hideZeros],
  );
  // Distinct problematic metrics with a per-metric event count, worst first —
  // the options of the "pick a metric" filter.
  const metricOpts = useMemo(() => {
    const m = new Map<string, { key: string; label: string; unit: string; n: number }>();
    for (const e of base) {
      const cur = m.get(e.metric);
      if (cur) cur.n++;
      else m.set(e.metric, { key: e.metric, label: e.label, unit: e.unit, n: 1 });
    }
    return [...m.values()].sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
  }, [base]);
  // If the selected metric vanishes (e.g. hide-zeros removed all its events or
  // the timeframe changed), fall back to "all" so the list never goes blank.
  useEffect(() => {
    if (metricFilter && !metricOpts.some(o => o.key === metricFilter)) setMetricFilter("");
  }, [metricOpts, metricFilter]);

  const events = useMemo(
    () => metricFilter ? base.filter(e => e.metric === metricFilter) : base,
    [base, metricFilter],
  );

  if (!data) return <div className="empty-state"><span className="spinner" /></div>;

  return (
    <>
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4 }}>{t("auto.filter_metric")}</div>
        <select value={metricFilter} style={{ width: "100%" }}
          onChange={e => { setMetricFilter(e.target.value); tg()?.HapticFeedback?.selectionChanged?.(); }}>
          <option value="">{t("auto.all_metrics")} ({base.length})</option>
          {metricOpts.map(o => (
            <option key={o.key} value={o.key}>{o.label}{o.unit ? `, ${o.unit}` : ""} ({o.n})</option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, margin: "0 0 10px" }}>
        <span style={{ fontSize: 12, color: "var(--hint)" }}>
          {t("auto.anomalies_hint", { n: data.count, floor: data.rel_floor_pct })}
          {data.truncated ? ` · ${t("auto.truncated")}` : ""}
        </span>
        <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={hideZeros} onChange={e => setHideZeros(e.target.checked)} />
          {t("auto.hide_zeros")}
        </label>
      </div>

      {events.length === 0 && <div className="empty-state">{t("auto.no_anomalies")}</div>}

      {events.map((e, i) => {
        const color = e.rel_pct >= 50 ? "#ff453a" : e.rel_pct >= 15 ? "#ff9500" : "#34c759";
        return (
          <div key={i} className="card" style={{ marginBottom: 8, borderLeft: `4px solid ${color}`, cursor: "pointer" }}
            onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); setSel(e); }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <b>{e.label}{e.unit ? <span style={{ color: "var(--hint)", fontWeight: 400 }}> {e.unit}</span> : null}</b>
              <span style={{ color, fontWeight: 700 }}>
                {e.direction === "high" ? "▲" : "▼"} {fmtNum(e.peak)} ({e.rel_pct}% {t("auto.off_normal")})
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 4 }}>
              {t("auto.normal")} {fmtNum(e.mean)} · {t("auto.when")}: {fmtTs(e.start)}
              {e.end !== e.start ? ` → ${fmtTime(e.end)}` : ""} · σ {e.max_z}
            </div>
            <div style={{ fontSize: 11, color: "var(--link, #2481cc)", marginTop: 3 }}>{t("auto.tap_for_details")} ›</div>
          </div>
        );
      })}

      {sel && (
        <SnapshotModal at={sel.start} metricKey={sel.metric} unit={unit}
          catalog={catalog} onClose={() => setSel(null)} />
      )}
    </>
  );
}

function SnapshotModal({ at, metricKey, unit, catalog, onClose }: {
  at: string; metricKey: string; unit: number; catalog: AutoCatalog | null; onClose: () => void;
}) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  const [snap, setSnap] = useState<AutoSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    tg()?.HapticFeedback?.impactOccurred("light");
    const p = new URLSearchParams({ unit: String(unit), at });
    api.get<AutoSnapshot>(`/api/auto/snapshot?${p.toString()}`).then(setSnap).catch((e: ApiError) => setErr(e.message));
  }, [at, unit]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }}
      onClick={onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "85vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{t("auto.snapshot_title")}</h2>
        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 12 }}>{fmtFull(at)}</div>
        {err && <div className="alert error">{err}</div>}
        {!snap && !err && <div className="empty-state"><span className="spinner" /></div>}
        {snap && (catalog?.groups ?? []).map(g => {
          const ms = (catalog?.metrics ?? []).filter(x => x.group === g.key && snap.metrics[x.key]);
          if (!ms.length) return null;
          return (
            <details key={g.key} className="cat-section" open>
              <summary className="cat-summary">
                <span className="cat-summary-arrow">▾</span>
                <span className="cat-summary-label">{g.label}</span>
              </summary>
              <table className="table" style={{ fontSize: 13 }}>
                <tbody>
                  {ms.map(x => {
                    const d = snap.metrics[x.key];
                    const hot = x.key === metricKey;
                    return (
                      <tr key={x.key} style={hot ? { background: "rgba(255,149,0,0.16)" } : undefined}>
                        <td>{hot ? "● " : ""}{x.label}{x.unit ? <span style={{ color: "var(--hint)" }}> {x.unit}</span> : null}</td>
                        <td className="num"><b>{fmtNum(d?.avg)}</b></td>
                        <td className="num" style={{ color: "var(--hint)" }}>{fmtNum(d?.min)}–{fmtNum(d?.max)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </details>
          );
        })}
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

// ---------------- Data (upload + imports + excel) ----------------

// Upload + import history (mode "upload", «Управление → Данные») or the Excel
// export for the current period (mode "excel", «Отчёты»).
export function AutoDataTab({ unit, qs, onErr, mode }: { unit: number; qs: string; onErr: (m: string) => void; mode: "upload" | "excel" | "history" }) {
  const { t } = useT();
  const [imports, setImports] = useState<AutoImportRow[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const pollRef = useRef<number | null>(null);
  const openFile = useOpenFile();

  const loadImports = () =>
    api.get<AutoImportRow[]>("/api/auto/imports").then(setImports).catch(() => undefined);

  useEffect(() => { loadImports(); return () => { if (pollRef.current) window.clearInterval(pollRef.current); }; }, []);

  const onPick = async (file: File) => {
    setUploading(true); setNote(null); onErr("");
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.upload<{ import_id: number }>(`/api/auto/import?unit=${unit}`, form);
      setNote(t("auto.upload_started"));
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(async () => {
        const rows = await api.get<AutoImportRow[]>("/api/auto/imports").catch(() => null);
        if (rows) {
          setImports(rows);
          const me = rows.find(x => x.id === r.import_id);
          if (me && me.status !== "processing") {
            if (pollRef.current) window.clearInterval(pollRef.current);
            setNote(me.status === "done" ? t("auto.upload_done", { rows: me.rows }) : t("auto.upload_error"));
            tg()?.HapticFeedback?.notificationOccurred(me.status === "done" ? "success" : "error");
          }
        }
      }, 3000);
    } catch (e) {
      onErr((e as ApiError).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exportXlsx = () => {
    api.get<Blob>(`/api/auto/export?${qs}`).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `auto-${Date.now()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    }).catch((e: ApiError) => onErr(e.message));
  };

  return (
    <>
      {mode === "upload" && (
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{t("auto.upload_title")}</div>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 10 }}>{t("auto.upload_hint")}</div>
        <input ref={fileRef} type="file" accept=".xlsx" disabled={uploading}
          onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
        {uploading && <div style={{ marginTop: 8 }}><span className="spinner" /> {t("auto.uploading")}</div>}
        {note && <div className="alert" style={{ marginTop: 8, background: "rgba(52,199,89,0.12)" }}>{note}</div>}
      </div>
      )}

      {mode === "excel" && (
        <div>
          <button className="btn excel" onClick={exportXlsx}>{t("auto.export_xlsx")}</button>
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 8 }}>{t("auto.export_hint")}</div>
        </div>
      )}
      {(mode === "upload" || mode === "history") && (<>

      <div style={{ fontSize: 12, color: "var(--hint)", margin: "0 0 6px" }}>{t("auto.imports_title")}</div>
      {!imports && <div className="empty-state"><span className="spinner" /></div>}
      {imports && imports.length === 0 && <div className="empty-state">{t("common.no_data")}</div>}
      {imports && imports.length > 0 && (
        <div className="card" style={{ overflowX: "auto" }}>
          <table className="table" style={{ fontSize: 12 }}>
            <thead><tr>
              <th>{t("auto.col_file")}</th><th>{t("auto.col_status")}</th><th>{t("auto.col_rows")}</th><th>{t("auto.col_period")}</th>
            </tr></thead>
            <tbody>
              {imports.map(im => {
                // a stored file opens the meter on its period, like «Последние файлы»
                const can = (im.status === "done" || im.status === "skipped") && !!im.from_ts && !!im.to_ts;
                return (
                  <tr key={im.id} onClick={can ? () => openFile({ ...im, kind: "meter", by_name: null } as FileRow) : undefined}
                    style={can ? { cursor: "pointer" } : undefined}>
                    <td style={{ maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{im.filename}</td>
                    <td title={im.status}>{im.status === "done" ? "✅" : im.status === "processing" ? "⏳" : im.status === "skipped" ? "＝" : "⚠️"}</td>
                    <td className="num">{im.rows || "—"}</td>
                    <td style={{ fontSize: 11 }}><HistoryPeriod from={im.from_ts} to={im.to_ts} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 6 }}>{t("files.row_hint")}</div>
        </div>
      )}
      </>)}
    </>
  );
}

// ---------------- Jumps (sudden step-changes) ----------------

function JumpsTab({ qs, unit, catalog, onErr }: {
  qs: string; unit: number; catalog: AutoCatalog | null; onErr: (m: string) => void;
}) {
  const { t } = useT();
  const [data, setData] = useState<AutoJumps | null>(null);
  const [metricFilter, setMetricFilter] = useState("");
  const [sel, setSel] = useState<AutoJump | null>(null);

  useEffect(() => {
    setData(null);
    api.get<AutoJumps>(`/api/auto/jumps?${qs}`).then(setData).catch((e: ApiError) => onErr(e.message));
  }, [qs]);

  const metricOpts = useMemo(() => {
    const m = new Map<string, { key: string; label: string; unit: string; n: number }>();
    for (const e of data?.events ?? []) {
      const cur = m.get(e.metric);
      if (cur) cur.n++;
      else m.set(e.metric, { key: e.metric, label: e.label, unit: e.unit, n: 1 });
    }
    return [...m.values()].sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
  }, [data]);
  useEffect(() => {
    if (metricFilter && !metricOpts.some(o => o.key === metricFilter)) setMetricFilter("");
  }, [metricOpts, metricFilter]);

  const events = useMemo(
    () => metricFilter ? (data?.events ?? []).filter(e => e.metric === metricFilter) : (data?.events ?? []),
    [data, metricFilter],
  );

  if (!data) return <div className="empty-state"><span className="spinner" /></div>;

  return (
    <>
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4 }}>{t("auto.filter_metric")}</div>
        <select value={metricFilter} style={{ width: "100%" }}
          onChange={e => { setMetricFilter(e.target.value); tg()?.HapticFeedback?.selectionChanged?.(); }}>
          <option value="">{t("auto.all_metrics")} ({data.events.length})</option>
          {metricOpts.map(o => (
            <option key={o.key} value={o.key}>{o.label}{o.unit ? `, ${o.unit}` : ""} ({o.n})</option>
          ))}
        </select>
      </div>

      <div style={{ fontSize: 12, color: "var(--hint)", margin: "0 0 10px" }}>
        {t("jumps.hint", { n: data.count })}{data.truncated ? ` · ${t("auto.truncated")}` : ""}
      </div>

      {events.length === 0 && <div className="empty-state">{t("jumps.none")}</div>}

      {events.map((e, i) => {
        const color = e.rel_pct >= 30 ? "#ff453a" : e.rel_pct >= 10 ? "#ff9500" : "#34c759";
        const up = e.direction === "up";
        return (
          <div key={i} className="card" style={{ marginBottom: 8, borderLeft: `4px solid ${color}`, cursor: "pointer" }}
            onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); setSel(e); }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <b>{e.label}{e.unit ? <span style={{ color: "var(--hint)", fontWeight: 400 }}> {e.unit}</span> : null}</b>
              <span style={{ color, fontWeight: 700 }}>
                {up ? "▲" : "▼"} {fmtNum(e.prev)} → {fmtNum(e.value)}
                <span style={{ fontWeight: 400 }}> ({up ? "+" : ""}{fmtNum(e.delta)} · {e.rel_pct}%)</span>
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 4 }}>🕒 {fmtFull(e.at)}</div>
            <div style={{ fontSize: 11, color: "var(--link, #2481cc)", marginTop: 3 }}>{t("auto.tap_for_details")} ›</div>
          </div>
        );
      })}

      {sel && (
        <SnapshotModal at={sel.at} metricKey={sel.metric} unit={unit}
          catalog={catalog} onClose={() => setSel(null)} />
      )}
    </>
  );
}
